import {
  FUNCTION_ACCESS,
  FUNCTION_PERMISSIONS,
  ROLE_LABELS,
  type AccessLevel,
  type RoleKey,
} from './functionAccess';

/**
 * Hitowa permissions.
 *
 * There is one sidebar for every kind of user. What an account sees is decided
 * by the permissions it holds — not by which portal it signed into, and not by
 * a role that swaps in a different menu tree.
 *
 * The grants come from the client's own matrix
 * (`KEiROW基幹システム_要望機能一覧.html`, tab 機能一覧): 134 functions × 6 roles,
 * transcribed into `functionAccess.ts`. One permission per 大分類.
 *
 * Effective account grants do not live here. PermissionContext loads them from
 * /me/permissions so a role label or this static source matrix can never become
 * an authorization decision.
 */

export type { AccessLevel, RoleKey };
export { ROLE_LABELS, FUNCTION_ACCESS, FUNCTION_PERMISSIONS };

/** Permission key for a 大分類, e.g. `利用者管理` -> `FN20`. */
export function permissionOf(category: string): string {
  const key = FUNCTION_PERMISSIONS[category];
  if (!key) {
    throw new Error(
      `No permission for 大分類 "${category}". It must match the 機能一覧 sheet exactly.`
    );
  }
  return key;
}

/** Permission keys for several 大分類 at once, for a menu entry. */
export const permissionsOf = (...categories: string[]): string[] => categories.map(permissionOf);

/**
 * Permissions a role holds.
 *
 * A role holds a 大分類 when it reaches at least one function inside it. `○` and
 * `△` both count: the sheet ships no legend, and both plainly mean the role can
 * open the screen — `△` narrows what it may do there, which is a separate
 * decision from whether the menu entry is visible.
 */
export function getRolePermissions(role: RoleKey): string[] {
  const held = new Set<string>();
  for (const fn of FUNCTION_ACCESS) {
    if (fn.access[role] !== 'none') held.add(fn.permission);
  }
  return [...held];
}

/** How far a role may go inside one function row. */
export function getFunctionAccess(role: RoleKey, functionNo: number): AccessLevel {
  return FUNCTION_ACCESS.find(fn => fn.no === functionNo)?.access[role] ?? 'none';
}
